'use client';

import React, { useState, useCallback } from 'react';
import { Box, TextField, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { useAgentChat } from '../../hooks/useAgentChat';
import { useAgentContext } from '../../context/AgentContext';
import { usePageContext } from '../../hooks/usePageContext';
import ContextChips from '../Context/ContextChips';
import InputSuggestions from './FollowUpSuggestions';
import type { ChatMessage } from '../../types/agent.types';

interface LastToolInfo {
  toolName: string;
  hasResults: boolean;
}

function getLastToolInfo(messages: ChatMessage[]): LastToolInfo | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role === 'assistant' && msg.toolCalls?.length) {
      const tc = msg.toolCalls[0];
      const result = tc.result as Record<string, unknown> | undefined;
      // Check if the tool produced meaningful results
      const hasResults =
        tc.status === 'success' &&
        result != null &&
        // catalog_search, pricing_calculator, offerings_browser: check totalResults
        (typeof result.totalResults === 'number' ? result.totalResults > 0 : true);
      return { toolName: tc.toolName, hasResults };
    }
  }
  return undefined;
}

export default function ChatInput() {
  const [input, setInput] = useState('');
  const { sendMessage, cancelResponse, isStreaming, connectionStatus } = useAgentChat();
  const { state } = useAgentContext();
  const pageContext = usePageContext();

  const isConnected = connectionStatus === 'connected';
  const canSend = input.trim().length > 0 && isConnected && !isStreaming;

  // Show suggestions when: has messages, last message is from assistant, not streaming, input is empty
  const showSuggestions =
    !isStreaming &&
    !state.error &&
    input.trim().length === 0 &&
    state.messages.length > 0 &&
    state.messages[state.messages.length - 1].role === 'assistant';

  const handleSend = useCallback(() => {
    if (!canSend) return;
    sendMessage(input);
    setInput('');
  }, [canSend, input, sendMessage]);

  const handleSuggestionSelect = useCallback((prompt: string) => {
    setInput(prompt);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <Box>
      <ContextChips />

      {showSuggestions &&
        (() => {
          const info = getLastToolInfo(state.messages);
          return (
            <InputSuggestions
              lastToolName={info?.toolName}
              hasResults={info?.hasResults ?? true}
              page={pageContext.page}
              onSelect={handleSuggestionSelect}
            />
          );
        })()}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={isConnected ? 'Ask about satellite imagery...' : 'Connecting...'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!isConnected}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'var(--tertiary-color)',
              borderRadius: '8px',
              color: 'var(--sw-white-color)',
              fontSize: '0.875rem',
              '& fieldset': {
                borderColor: 'rgba(244, 246, 255, 0.1)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(244, 246, 255, 0.2)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--primary-color)',
              },
            },
            '&.Mui-disabled': {
              color: 'var(--sw-white-color-90)',
              WebkitTextFillColor: 'var(--sw-white-color-90)',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: 'var(--sw-white-color)',
              opacity: 0.7,
            },
            '& .MuiOutlinedInput-input.Mui-disabled::placeholder': {
              color: 'var(--sw-white-color)',
              opacity: 0.5,
              WebkitTextFillColor: 'rgba(244, 246, 255, 0.5)',
            },
          }}
        />
        {isStreaming ? (
          <IconButton
            aria-label="Stop response"
            onClick={cancelResponse}
            sx={{
              bgcolor: '#d0342c',
              color: '#fff',
              borderRadius: '20px',
              width: 40,
              height: 40,
              '&:hover': { bgcolor: '#b82a24' },
            }}
          >
            <StopCircleIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton
            aria-label="Send message"
            onClick={handleSend}
            disabled={!canSend}
            sx={{
              bgcolor: canSend ? 'var(--primary-color)' : 'rgba(71, 225, 188, 0.3)',
              color: '#212b35',
              borderRadius: '20px',
              width: 40,
              height: 40,
              '&:hover': { bgcolor: 'var(--sw-sider-btn-bg)' },
              '&.Mui-disabled': {
                color: '#212b35',
                bgcolor: 'rgba(71, 225, 188, 0.3)',
              },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
